import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;

export const CLIPCHAMP_CONTENT: CompareContent = {
  slug: "crayo-vs-clipchamp",
  competitorSlug: "clipchamp",
  title: "Crayo vs Clipchamp",
  seoTitle: "Crayo vs Clipchamp: Free Editor or AI Clipping Engine?",
  seoDescription:
    "Crayo vs Clipchamp, with dated prices. Clipchamp is a free 1080p editor with no auto clipping. Crayo finds, ranks and reframes clips from one paid plan.",
  cardVerdict:
    "Better than Crayo for free 1080p editing and 4K with Microsoft 365, behind on auto clipping and short-form formats.",
  bestFor: "Windows users weighing free editing against clipping",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Clipchamp is the better pick if you want a free, watermark-free 1080p editor and are willing to find and cut every clip yourself. Crayo is the better pick if you clip podcasts and streams and want long videos turned into ranked short clips automatically, with face-aware reframing, 32 caption styles and a timeline editor, and you will pay ${money(hobby.monthly)} a month for that. The evidence is below.`,
  pickCrayoIf: [
    "You clip long videos for Shorts, Reels or TikTok: AutoClip finds and ranks the moments, cuts 1 to 20 clips per run and reframes faces. Clipchamp's own gaming page lists auto highlights as not present.",
    "You want 32 short-form caption styles, keyword highlight and four clip layouts without building each title by hand in a timeline.",
    `You keep a few clips from each long source and pay per finished clip: Hobby gives ${hobby.credits} credits a month, every plan opens clips in the timeline editor, and a public API with 34 endpoints is included. Clipchamp has no API.`,
  ],
  pickThemIf: [
    "You want a free editor with no watermark, no credits and unlimited 1080p exports. Clipchamp Free is $0 with a personal Microsoft account; Crayo has no free plan.",
    "You already pay for Microsoft 365 Personal or Family: Premium adds 4K export, premium stock and a brand kit at no extra cost. Crayo exports at 1080p maximum.",
    "You edit Xbox clips or record your screen and webcam: Clipchamp imports Xbox captures directly and ships preinstalled on Windows 11.",
  ],
  wins: [
    {
      claim: "Crayo finds and cuts clips automatically; Clipchamp does not clip at all.",
      evidence:
        "Clipchamp's gaming video editor page, checked Oct 8, 2026, lists Auto highlights as Not present and says highlight videos are built manually. Crayo AutoClip takes YouTube and TikTok links or a file upload of up to 3 hours and returns 1 to 20 ranked clips of 15 to 120 seconds.",
      href: "https://clipchamp.com/en/gaming-video-editor/",
    },
    {
      claim: "Crayo ranks moments and reframes faces; Clipchamp has neither.",
      evidence:
        "Clipchamp's features page, checked Oct 8, 2026, offers manual resize and crop for aspect ratios and no face tracking or virality ranking. Crayo's face-aware camera keeps the speaker framed at 9:16, 1:1 or 4:5, and Find Viral Moments surfaces up to 50 ranked moments to review before cutting.",
      href: "https://clipchamp.com/en/features/",
    },
    {
      claim: "Crayo ships short-form caption presets; Clipchamp ships animated titles.",
      evidence:
        "Clipchamp's homepage advertises AI subtitles in over 80 languages and animated text, but no karaoke or word-highlight presets. Crayo has 32 caption styles with keyword highlight, applied to the clip in one click, across 56 spoken languages.",
      href: "https://clipchamp.com/en/",
    },
    {
      claim: "Crayo has a public API with 34 endpoints and webhooks; Clipchamp has no API.",
      evidence:
        "No developer or API page exists on clipchamp.com as of Oct 8, 2026, and the Work version is licensed per Microsoft 365 tenant rather than exposed to code. Crayo's API covers autoclip, exports, projects, transcripts, voiceover and webhooks with a test endpoint on every plan, one key per account, with docs at crayo.ai/developers.",
      href: "https://crayo.ai/developers",
    },
  ],
  concessions: [
    {
      claim: "Clipchamp is free, watermark-free and unmetered at 1080p.",
      evidence:
        `The pricing page, checked Oct 8, 2026, lists Free at $0 with unlimited exports, up to 1080p, watermark-free exports and free stock. Crayo has no free plan and no trial; the entry plan is Hobby at ${money(hobby.monthly)} a month.`,
      href: "https://clipchamp.com/en/pricing/",
    },
    {
      claim: "Clipchamp exports 4K for anyone paying for Microsoft 365.",
      evidence:
        "Premium includes up to 4K UHD export and is bundled with Microsoft 365 Personal at $9.99 a month or $99.99 a year on microsoft.com, checked Oct 8, 2026. Crayo exports at 1080p maximum.",
      href: "https://www.microsoft.com/en-us/microsoft-365/buy/compare-all-microsoft-365-products",
    },
    {
      claim: "Clipchamp is preinstalled on Windows 11, imports Xbox clips and records screen and webcam.",
      evidence:
        "Clipchamp has shipped with Windows 11 since March 2022, and the gaming page says you can access Xbox clips with no file transfers. Crayo is a browser app with no Xbox import and no screen recorder.",
      href: "https://clipchamp.com/en/gaming-video-editor/",
    },
    {
      claim: "Clipchamp is a mature multi-track editor with 80+ caption languages and text-to-speech.",
      evidence:
        "The features page lists green screen, audio editing, GIF maker, silence remover, noise remover and background removal; the homepage lists AI subtitles in over 80 languages. Crayo transcribes 56 spoken languages.",
      href: "https://clipchamp.com/en/features/",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes; AutoClip finds, ranks and cuts the 32 clips`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Free",
      cost: "$0",
      usage:
        "Nothing is metered, so the 32 clips cost nothing. Every one of them is found and cut by hand: you scrub four 60-minute sources, mark each clip's in and out points, crop to 9:16, add captions and export, 32 times.",
      note: "Per the Clipchamp pricing page, checked Oct 8, 2026. Premium changes the export cap to 4K, not the workflow.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 2-hour gameplay stream VOD with facecam owned by Crayo",
    plans:
      "Clipchamp Premium via Microsoft 365 Personal and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Clipchamp has no credits and no minutes, so nothing rolls over or expires. Premium is not sold on its own to new buyers: it comes with Microsoft 365 Personal ($9.99/mo or $99.99/yr) or Family ($12.99/mo or $129.99/yr), and Clipchamp's legacy standalone plans were retired. Refund and cancellation terms are not on the Clipchamp pricing page.",
  faqs: [
    {
      question: "Is Crayo cheaper than Clipchamp?",
      answer:
        `No. Clipchamp Free costs $0 with a personal Microsoft account and exports unlimited 1080p video without a watermark, per its pricing page checked Oct 8, 2026. Crayo has no free plan; Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} workflow credits. What you pay Crayo for is the clipping work: AutoClip finds, ranks, reframes and captions the clips, and Clipchamp leaves all of that to you. If your time is free and you enjoy editing, Clipchamp is the cheaper tool. If you are producing clips every week, the credit buys the hours.`,
    },
    {
      question: "Does Clipchamp have auto clipping?",
      answer:
        "No. Clipchamp's gaming video editor page, checked Oct 8, 2026, states that auto highlights are not present and that highlight videos are built manually. Its AI auto compose feature assembles a video from media you select; it does not scan a long recording for moments. Clipchamp also has no virality ranking and no face-aware reframing. Crayo AutoClip takes a YouTube or TikTok link or a file upload up to 3 hours and returns 1 to 20 ranked clips with the speaker framed, so the comparison is a general editor against a clipping engine.",
    },
    {
      question: "Does Crayo have a free plan like Clipchamp?",
      answer:
        `No. Crayo has no free plan and no trial. The only free Crayo products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. The entry plan is Hobby at ${money(hobby.monthly)} a month, and every plan includes AutoClip, the timeline editor and the API, with one credit per finished clip. Clipchamp Free is a full editor at $0 with unlimited watermark-free 1080p exports and 5 GB of cloud storage. If a free tier is the deciding factor, Clipchamp wins that row outright.`,
    },
    {
      question: "How much does Clipchamp Premium cost?",
      answer:
        "Clipchamp's pricing page prints no dollar amount for Premium. As of Oct 8, 2026, Premium features are included with Microsoft 365 Personal, which costs $9.99 a month or $99.99 a year on microsoft.com, and with Family at $12.99 a month or $129.99 a year. Premium adds 4K export, premium stock and effects, a single brand kit and 1 TB of OneDrive storage. Clipchamp retired its legacy standalone Creator, Business and Business Platinum plans, and we could not verify a standalone Premium price, so third-party figures are not used here.",
    },
    {
      question: "Can Clipchamp make YouTube Shorts and TikToks?",
      answer:
        "Yes, by hand. Clipchamp has 9:16 aspect presets, AI subtitles in over 80 languages, animated titles, stock media and direct sharing to YouTube, TikTok and LinkedIn. What it lacks is the short-form automation: no auto clipping, no moment ranking, no face tracking when you crop a wide shot to vertical, and no karaoke or word-highlight caption presets. Crayo is built for that output: four layouts including streamer and split, three vertical and square aspects, 32 caption styles and ranked clips from one run.",
    },
    {
      question: "Which is better for streamers, Crayo or Clipchamp?",
      answer:
        "It depends on the source. Clipchamp imports Xbox clips with no file transfer and records screen and webcam, which suits console players who already have short captures and want to trim and caption them. Crayo takes a YouTube or TikTok link or a file upload up to 3 hours and 4 GB and returns ranked clips with a streamer layout that keeps gameplay and facecam together, plus action and no-talking content modes for gameplay without commentary. Neither product imports Twitch or Kick VODs directly as of Oct 8, 2026, so a stream archive reaches either tool as a download or, for Crayo, a YouTube upload.",
    },
    {
      question: "Does Crayo post to TikTok and YouTube like Clipchamp?",
      answer:
        "No. Crayo exports the file and you post it yourself; there is no scheduler and no direct share button. Clipchamp shares a finished video to YouTube, TikTok and LinkedIn from its export screen, but it has no scheduler either, so neither product queues posts for later. If one-click sharing after export matters, Clipchamp wins that row. Crayo's View Tracker follows Instagram, YouTube and TikTok creators for posting cadence and viral outliers, on 100 to 2,500 monthly tracker credits by plan; it is not post analytics and not a scheduler, and this page does not claim otherwise.",
    },
    {
      question: "Does Clipchamp have a mobile app?",
      answer:
        "No. Microsoft ended support for the Clipchamp iOS app in June 2026 with no replacement and no way to export videos stored in the app, and there was never an Android app. Clipchamp runs in the browser and as a Windows app that ships preinstalled on Windows 11; the homepage lists only those two. Crayo is a browser app and makes no mobile app claim on this page. For editing on a phone, neither product is the answer today, so a Clipchamp project started on a Windows PC stays on the desktop, and Crayo's clips are made in a desktop browser and downloaded.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Clipchamp prices checked Oct 8, 2026: Free $0 with unlimited 1080p exports, Premium bundled with Microsoft 365 Personal at $9.99/mo or $99.99/yr, no standalone Premium price published.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-capcut", "crayo-vs-veed", "crayo-vs-kapwing"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "clipchamp alternative",
    "clipchamp vs crayo",
    "crayo vs clipchamp",
    "clipchamp auto clipping",
    "clipchamp for shorts",
    "clipchamp premium price",
  ],
};
